import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import * as Sentry from "@sentry/react";
import { 
  Loader2} from 'lucide-react';
import { useAuth } from '../components/auth/AuthContext';

// Import individual tab components
import DepositsTab from '../components/billing/DepositsTab';
import DeploymentBillingTab from '../components/billing/DeploymentBillingTab';
import VolumeTab from '../components/billing/VolumeTab';
import SnapshotTab from '../components/billing/SnapshotTab';

// Import shared utilities
import { logError, logInfo, logUserAction, logPerformance } from '../components/billing/BillingUtils';

// ✅ ADD THIS IMPORT - NotificationProvider
import { NotificationProvider } from '../Notifications';

const BillingPage = () => {
  const navigate = useNavigate();
const [activeTab, setActiveTab] = useState('deposits');
  const [barrackId, setBarrackId] = useState('');
  const { handleAuthError } = useAuth();

  // Set Sentry context for BillingPage
  useEffect(() => {
    Sentry.setContext("billing-page", {
      activeTab,
      barrackId,
      currentUrl: window.location.href
    });
  }, [activeTab, barrackId]);

  // Component mount logging
  useEffect(() => {
    logInfo('BillingPage component mounted', {
      initialTab: activeTab,
      userAgent: navigator.userAgent
    });
  }, []);

  useEffect(() => {
    const startTime = performance.now();
    
    logInfo("BillingPage: Fetching user profile for barrack_id");
 api.getUserAuthProfile()
    .then(response => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (response.success) {
        setBarrackId(response.barrack_id);
        
        logPerformance('fetch_user_profile', duration, {
          success: true,
          barrackId: response.barrack_id
        });
        
        logInfo('BillingPage: User profile fetched successfully', {
          barrackId: response.barrack_id,
          duration: `${duration.toFixed(2)}ms`
        });
      } else {
        logError('BillingPage: API call successful but returned success:false', {
          response,
          duration: `${duration.toFixed(2)}ms`
        });
      }
    })
    .catch(err => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      if (err.status === 401) {
        handleAuthError();
        return;
      }
      
      logError('BillingPage: Error fetching user profile', {
        code: err.code,
        message: err.error,
        duration: `${duration.toFixed(2)}ms`
      }, err);
      
      const storedId = localStorage.getItem('barrack_id');
      if (storedId) {
        logInfo('BillingPage: Falling back to localStorage barrack_id', {
          storedId
        });
        setBarrackId(storedId);
      } else {
        logError('BillingPage: No barrack_id found in localStorage', {
          localStorage: Object.keys(localStorage)
        });
      }
    });
      
  }, []);

  // Handle tab changes with logging
  const handleTabChange = (newTab) => {
    logUserAction('billing_tab_changed', {
      previousTab: activeTab,
      newTab,
      barrackId
    });
    
    setActiveTab(newTab);
  };

  // Component unmount cleanup
  useEffect(() => {
    return () => {
      logInfo('BillingPage component unmounted', {
        finalTab: activeTab,
        barrackId,
        sessionDuration: performance.now()
      });
    };
  }, []);

  return (
    <div className="bg-black min-h-screen">
      {/* Header with title */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '24px 40px 16px 40px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
       <div style={{
        display: 'flex',
        alignItems: 'center',
        marginLeft: '2cm'
       }}>
         <div style={{
  marginRight: '16px',
  color: '#e0eacc'
}}>
  <svg xmlns="http://www.w3.org/2000/svg" style={{ width: '24px', height: '24px' }} viewBox="0 0 20 20" fill="currentColor">
    <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
    <path fillRule="evenodd" d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" clipRule="evenodd" />
  </svg>
</div>
          <div>
            <h1 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'white',
              margin: 0
            }}>Billing & Transactions</h1>
          
          </div>
        </div>
      </div>

     {/* Tabs */}
<div style={{
  display: 'flex',
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  padding: '0 40px',
  margin: '0 auto',
  width: '85%',
  overflowX: 'auto',
  whiteSpace: 'nowrap',
  scrollbarWidth: 'thin',
  scrollbarColor: '#333333 transparent'
}}>
       
        <button 
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'deposits' ? 'white' : '#a0a0a0',
            fontSize: '15px',
            fontWeight: '500',
            padding: '16px 28px',
            cursor: 'pointer',
            position: 'relative',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderBottom: activeTab === 'deposits' ? '2px solid #e0eacc' : 'none'
          }}
          onClick={() => handleTabChange('deposits')}
        >
          Credits
        </button>
        <button 
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'deploymentBilling' ? 'white' : '#a0a0a0',
            fontSize: '15px',
            fontWeight: '500',
            padding: '16px 28px',
            cursor: 'pointer',
            position: 'relative',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderBottom: activeTab === 'deploymentBilling' ? '2px solid #e0eacc' : 'none'
          }}
          onClick={() => handleTabChange('deploymentBilling')}
        >
          Deployment Billing
        </button>
        <button 
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'volumeBilling' ? 'white' : '#a0a0a0',
            fontSize: '15px',
            fontWeight: '500',
            padding: '16px 28px',
            cursor: 'pointer',
            position: 'relative',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderBottom: activeTab === 'volumeBilling' ? '2px solid #e0eacc' : 'none'
          }}
          onClick={() => handleTabChange('volumeBilling')}
        >
          Volume Billing
        </button>
        <button 
          style={{
            background: 'none',
            border: 'none',
            color: activeTab === 'snapshotBilling' ? 'white' : '#a0a0a0',
            fontSize: '15px',
            fontWeight: '500',
            padding: '16px 28px',
            cursor: 'pointer',
            position: 'relative',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            borderBottom: activeTab === 'snapshotBilling' ? '2px solid #e0eacc' : 'none'
          }}
          onClick={() => handleTabChange('snapshotBilling')}
        >
          Snapshot Billing
        </button>
      
      </div>

      {/* ✅ UPDATED: Tab Content with NotificationProvider wrapper */}
      <div style={{
        padding: '28px 0',
        width: '85%',
        margin: '0 auto'
      }}>
        <NotificationProvider>
          {barrackId ? (
            <>
              {activeTab === 'deposits' && <DepositsTab barrackId={barrackId} />}
              {activeTab === 'deploymentBilling' && <DeploymentBillingTab barrackId={barrackId} />}
              {activeTab === 'volumeBilling' && <VolumeTab barrackId={barrackId} />}
              {activeTab === 'snapshotBilling' && <SnapshotTab barrackId={barrackId} />}
            </>
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px 0',
              backgroundColor: '#1a1a1a',
              borderRadius: '10px',
              border: '1px solid #333333',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
              color: '#a0a0a0',
              fontSize: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px'
            }}>
              <Loader2 className="animate-spin" size={24} />
              Loading user information...
            </div>
          )}
        </NotificationProvider>
      </div>
    </div>
  );
};

export default BillingPage;